/*
 * PRD-037 — Modal de contratación del paso de plan del claim (app SEPARADA).
 *
 * En el claim el ÚNICO método de pago ofrecido es TARJETA (la transferencia se oculta a propósito:
 * el dueño recién activado contrata en línea con tarjeta + 3DS). Por eso NO se monta el
 * PaymentMethodSelector compartido del panel.
 *
 * Sin paso de "confirmar método": mostrar una tarjeta que dice "Tarjeta de crédito o débito" como
 * si fuera una elección, cuando es la ÚNICA opción, no aporta nada — el clic en "Elegir <plan>"
 * (ClaimPlanStep) YA es la decisión deliberada del dueño.
 *
 * SÍ hay un paso previo, presentado como RESUMEN de contratación (no como stopper): título + precio,
 * un recap de lo que incluye el plan (pedidos + características top, reusando los mismos datos que la
 * card de ClaimPlanStep) y un ÚNICO CTA "Continuar al pago". El código de descuento es OPCIONAL y va
 * COLAPSADO detrás de un link ("+ Agregar código de descuento"): así el modal no muestra un input
 * vacío que parezca obligatorio.
 *
 * El cupón se PREVISUALIZA antes de crear la suscripción: al escribir el código y pulsar "Aplicar",
 * validateCoupon (POST /v1/suscriptions/validate-coupon) resuelve el promotion_code y devuelve el
 * descuento SIN crear suscripción ni cobrar. Así el dueño VE el ahorro (línea verde + precio con
 * descuento tachando el original en el resumen) y puede probar códigos sin generar suscripciones
 * incomplete en Stripe. La suscripción se crea UNA sola vez, al continuar al pago: el STRING del
 * cupón viaja en convertFreemiumToPlan (el backend vuelve a resolverlo/aplicarlo; el preview es solo
 * lectura, no cobra).
 *
 * Reusa los presentacionales del panel (StripePaymentForm / isStripeConfigured) sobre
 * convertFreemiumToPlan (axiosInstance directo). Los errores se muestran inline (el partner no
 * monta <Toaster>).
 */
import { useEffect, useRef, useState } from 'react';
import { Modal } from '@plick/ui';
import { IconCheck, IconLock, IconPaperBag, IconPlus, IconTag } from '@tabler/icons-react';
import { V4 } from '@components/v4/tokens';
import { Button } from '@components/v4/Button/Button';
import { StripePaymentForm, isStripeConfigured } from '@pages/Subscriptionsv2/components/StripePaymentForm';
import type { MetodoPago } from '@pages/Subscriptionsv2/hooks/useActivatePlan';
import type { AvailableSubscription } from '@interfaces/general';
import { formatToCurrency } from '@utils/formats';
import { convertFreemiumToPlan, validateCoupon, type ValidateCouponResponse } from './useClaimPlans';

// En el claim el método de pago es siempre tarjeta (la transferencia no se ofrece).
const METODO_PAGO: MetodoPago = 'tarjeta';

// Cuántas características listamos en el resumen (las top). El plan completo se ve en la card del
// paso anterior (ClaimPlanStep) y en el panel; aquí solo confirmamos lo esencial de la contratación.
const MAX_SUMMARY_FEATURES = 2;

// El contrato de validate-coupon devuelve los montos en CENTAVOS MXN; formatToCurrency espera pesos.
const centsToPesos = (cents: number): number => cents / 100;

// True si el preview confirma un cupón usable: existe, está activo y aplica al producto del plan.
const isCouponUsable = (r: ValidateCouponResponse | null): boolean => r !== null && r.valid && r.applies_to_plan;

// True si el preview no solo es usable sino que trae AMBOS montos numéricos para pintar y restar.
// Se exige original_price además de discounted_price: el tipo los declara nullable por separado, así
// que sin este guard un preview con discounted_price pero original_price null produciría un descuento
// negativo (0 − discounted) y un precio original tachado en $0.
const hasDiscountedPrice = (r: ValidateCouponResponse | null): boolean =>
  isCouponUsable(r) && r !== null && r.discounted_price !== null && r.original_price !== null;

// Resultado inline del preview de cupón. Se extrae del modal para mantener baja la complejidad del
// componente principal: aquí vive toda la ramificación de estados del preview (usable / no aplica /
// inválido). Los montos del contrato vienen en centavos → pesos para formatToCurrency.
const CouponPreviewResult = ({ preview }: { preview: ValidateCouponResponse }) => {
  if (hasDiscountedPrice(preview)) {
    const discountCents = (preview.original_price ?? 0) - (preview.discounted_price ?? 0);
    return (
      <p
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          margin: '8px 0 0',
          fontSize: 13,
          fontWeight: 600,
          color: V4.successDark,
          lineHeight: 1.4,
        }}
      >
        <IconTag size={15} stroke={2} style={{ flexShrink: 0 }} />
        Descuento aplicado: −{formatToCurrency(centsToPesos(discountCents))} · Total:{' '}
        {formatToCurrency(centsToPesos(preview.discounted_price ?? 0))}
      </p>
    );
  }

  // Cupón existe pero no aplica a este plan → aviso ámbar; cualquier otro caso → inválido/expirado.
  const notApplicable = preview.valid && !preview.applies_to_plan;
  return (
    <p
      style={{
        margin: '8px 0 0',
        fontSize: 12,
        color: notApplicable ? V4.warningDark : V4.danger,
        lineHeight: 1.4,
      }}
      role="alert"
    >
      {notApplicable ? 'Este código no aplica a este plan' : 'Código inválido o expirado'}
    </p>
  );
};

interface ClaimPlanModalProps {
  plan: AvailableSubscription;
  isOpen: boolean;
  onClose: () => void;
  /** Se invoca tras una contratación exitosa (tarjeta confirmada / 3DS resuelto). */
  onActivated: () => void;
}

export const ClaimPlanModal = ({ plan, isOpen, onClose, onActivated }: ClaimPlanModalProps) => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Código de descuento OPCIONAL que teclea el dueño. Viaja en la MISMA llamada que crea la
  // suscripción (convertFreemiumToPlan); por eso se captura ANTES de disparar el pago.
  const [coupon, setCoupon] = useState('');
  // El campo de cupón arranca COLAPSADO para que el modal se lea como un resumen de contratación, no
  // como un stopper con un input vacío que parezca obligatorio. Se expande al pulsar el link.
  const [couponExpanded, setCouponExpanded] = useState(false);
  // Preview del cupón (resultado de validate-coupon). Es SOLO LECTURA: no crea suscripción. Cuando
  // es usable, el resumen muestra la línea verde + el precio con descuento.
  const [couponPreview, setCouponPreview] = useState<ValidateCouponResponse | null>(null);
  // Código (normalizado) que produjo el preview vigente. Si el dueño edita el input tras validar, el
  // preview deja de corresponder al código actual y volvemos al precio lleno hasta un nuevo "Aplicar".
  const [couponPreviewCode, setCouponPreviewCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  // Evita re-disparar convertFreemiumToPlan (crea la suscripción incomplete en Stripe) más de una
  // vez — guard síncrono, no depende del estado async.
  const fetchedRef = useRef(false);
  // Cupón (STRING normalizado) con el que se creó el client_secret vigente. Permite REUSAR el pago
  // ya montado si el dueño reabre el paso sin cambiar el código, en lugar de re-crear la suscripción.
  const paidCouponRef = useRef<string | null>(null);

  const cardConfigured = isStripeConfigured();

  // Normaliza el cupón para comparar/enviar: trim + mayúsculas (el input ya fuerza uppercase, pero
  // el trim evita que un espacio cuente como "cambió el código").
  const normalizedCoupon = coupon.trim().toUpperCase();

  const fetchClientSecret = (couponCode?: string) => {
    if (fetchedRef.current || !cardConfigured) return;
    fetchedRef.current = true;
    setError(null);
    setIsLoading(true);
    convertFreemiumToPlan(plan.id, METODO_PAGO, couponCode)
      .then((response) => {
        if (response.client_secret) {
          setClientSecret(response.client_secret);
          // Recordamos con qué cupón se creó este pago para poder reusarlo si el dueño reabre el
          // paso sin cambiar el código (evita re-crear la suscripción). Guardamos el string
          // normalizado, o '' si no hubo cupón.
          paidCouponRef.current = couponCode?.trim().toUpperCase() ?? '';
        } else {
          setError('No se recibió la información de pago. Intenta de nuevo.');
        }
      })
      .catch((err: unknown) => {
        const message =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          (err as Error)?.message ||
          'No se pudo activar el plan. Intenta de nuevo.';
        setError(message);
      })
      .finally(() => setIsLoading(false));
  };

  // Preview del cupón: valida el código SIN crear suscripción y muestra el resultado inline. No toca
  // fetchedRef ni convertFreemium — es solo lectura, así el dueño puede probar códigos sin generar
  // suscripciones incomplete en Stripe.
  const handleApplyCoupon = () => {
    if (!normalizedCoupon || couponLoading) return;
    setCouponLoading(true);
    setCouponPreview(null);
    setCouponPreviewCode(normalizedCoupon);
    validateCoupon(normalizedCoupon, plan.id)
      .then((result) => setCouponPreview(result))
      .catch((err: unknown) => {
        // Ante fallo de red/500 no bloqueamos el pago: mostramos un preview inválido con mensaje
        // neutro. El código seguirá viajando en convertFreemium (el backend vuelve a resolverlo).
        const message =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'No se pudo validar el código. Puedes continuar; se aplicará al pagar si es válido.';
        setCouponPreview({
          valid: false,
          applies_to_plan: false,
          type: null,
          percent_off: null,
          amount_off: null,
          original_price: null,
          discounted_price: null,
          message,
        });
      })
      .finally(() => setCouponLoading(false));
  };

  // Al cerrar el modal reseteamos el flujo para que la próxima apertura empiece en el paso del
  // código. NO se dispara convertFreemium automáticamente: el dueño primero teclea el código
  // (opcional) y presiona "Continuar al pago", que crea la suscripción con el coupon.
  useEffect(() => {
    if (!isOpen) {
      fetchedRef.current = false;
      paidCouponRef.current = null;
      setClientSecret(null);
      setError(null);
      setCoupon('');
      setCouponExpanded(false);
      setCouponPreview(null);
      setCouponPreviewCode('');
      setCouponLoading(false);
      // Se resetea también isLoading: si el modal se cierra con una llamada en vuelo, sin esto
      // quedaría "Preparando tu pago…" pegado y una respuesta stale podría inyectar un
      // client_secret de la interacción anterior al reabrir (el guard de render depende de isLoading).
      setIsLoading(false);
    }
  }, [isOpen, plan.id]);

  const handleClose = () => {
    fetchedRef.current = false;
    paidCouponRef.current = null;
    setClientSecret(null);
    setError(null);
    setCoupon('');
    setCouponExpanded(false);
    setCouponPreview(null);
    setCouponPreviewCode('');
    setCouponLoading(false);
    setIsLoading(false);
    onClose();
  };

  // Paso "aplicar código (opcional) → continuar": crea la suscripción con el coupon tecleado.
  // Reuso: si ya hay un client_secret vigente creado con el MISMO cupón (mismo plan, misma sesión de
  // modal), no re-disparamos convertFreemium — el pago ya montado sigue válido. Si el dueño cambió el
  // código respecto al que generó el pago, hay que re-crear la suscripción para reflejar el descuento
  // correcto, así que reseteamos el guard. (El fetchedRef ya evita el doble disparo dentro de una
  // misma creación; este bloque cubre el reuso/cambio explícito entre intentos.)
  const handleContinueToPayment = () => {
    if (clientSecret && paidCouponRef.current === normalizedCoupon) return;
    if (paidCouponRef.current !== null && paidCouponRef.current !== normalizedCoupon) {
      fetchedRef.current = false;
      setClientSecret(null);
    }
    fetchClientSecret(normalizedCoupon);
  };

  const handleRetry = () => {
    fetchedRef.current = false;
    // Reintenta con el mismo código que el dueño ya tecleó.
    fetchClientSecret(normalizedCoupon);
  };

  // ¿El preview vigente confirma un descuento aplicable AL cupón actualmente tecleado? Solo entonces
  // pintamos el precio con descuento en el header (si el dueño editó el código tras validar, el
  // preview deja de corresponder y volvemos al precio lleno hasta que vuelva a "Aplicar").
  const previewMatchesInput = couponPreview !== null && couponPreviewCode === normalizedCoupon;
  const activeDiscount = previewMatchesInput && hasDiscountedPrice(couponPreview) ? couponPreview : null;
  const periodLabel = plan.periodo_pago === 'anual' ? 'pago anual' : 'pago mensual';

  return (
    <Modal padding="0rem" maxHeight="90vh" maxWidth="480px" isOpen={isOpen} onClose={handleClose}>
      <div style={{ padding: 24, fontFamily: 'Poppins, sans-serif' }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: V4.primary }}>Activar {plan.nombre}</h3>
        {activeDiscount ? (
          // Con un cupón validado y aplicable, el header muestra el precio con descuento (nuevo
          // total en verde) tachando el original, para que el dueño VEA que el código jaló. Los
          // montos vienen del contrato en centavos (fuente de verdad del descuento) → pesos.
          <p style={{ margin: '4px 0 16px', fontSize: 13, color: V4.grayUltraDark, lineHeight: 1.5 }}>
            <span style={{ textDecoration: 'line-through', marginRight: 8 }}>
              {formatToCurrency(centsToPesos(activeDiscount.original_price ?? 0))}
            </span>
            <strong style={{ color: V4.successDark, fontSize: 15 }}>
              {formatToCurrency(centsToPesos(activeDiscount.discounted_price ?? 0))}
            </strong>{' '}
            · {periodLabel}
          </p>
        ) : (
          typeof plan.precio === 'number' && (
            <p style={{ margin: '4px 0 16px', fontSize: 13, color: V4.grayUltraDark }}>
              {formatToCurrency(plan.precio)} · {periodLabel}
            </p>
          )
        )}

        {!cardConfigured && (
          <p style={{ margin: 0, fontSize: 12, color: V4.warningDark, lineHeight: 1.4 }}>
            El pago con tarjeta no está disponible por ahora. Vuelve a intentarlo más tarde o contacta a soporte.
          </p>
        )}

        {/* Paso 1: resumen de contratación. Se lee como un recap del plan (pedidos + features top),
            con el código de descuento como acción OPCIONAL y colapsada, y un único CTA al pago. */}
        {cardConfigured && !clientSecret && !isLoading && !error && (
          <>
            {/* Separador bajo el título/precio (que ya existen arriba). */}
            <div style={{ borderTop: `1px solid ${V4.grayLight}`, margin: '4px 0 14px' }} />

            {/* Resumen del plan: pedidos incluidos + las 2 características top. Reusa los mismos
                datos que la card del paso anterior (plan.pedidos_maximos / plan.caracteristicas). */}
            <ul
              style={{
                listStyle: 'none',
                margin: '0 0 16px',
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 9,
              }}
            >
              {typeof plan.pedidos_maximos === 'number' && plan.pedidos_maximos > 0 && (
                <li style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: V4.primary }}>
                  <IconPaperBag size={16} stroke={2} color={V4.accent} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>
                    <strong>{plan.pedidos_maximos} pedidos</strong> mensuales
                  </span>
                </li>
              )}
              {plan.caracteristicas?.slice(0, MAX_SUMMARY_FEATURES).map((feature, index) => (
                <li
                  key={index}
                  style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: V4.primary }}
                >
                  <IconCheck size={16} stroke={2.5} color={V4.success} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>{feature.descripcion}</span>
                </li>
              ))}
            </ul>

            {/* Código de descuento OPCIONAL: colapsado por defecto (link sutil). Al expandir muestra
                el input + botón "Aplicar" que PREVISUALIZA el descuento (validate-coupon, solo
                lectura) sin crear suscripción. El string sigue viajando en convertFreemiumToPlan al
                continuar; el backend lo vuelve a resolver/aplicar. */}
            {couponExpanded ? (
              <div style={{ marginBottom: 16 }}>
                <label
                  htmlFor="claim-coupon"
                  style={{ display: 'block', margin: '0 0 6px', fontSize: 13, fontWeight: 600, color: V4.primary }}
                >
                  Código de descuento
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    id="claim-coupon"
                    type="text"
                    value={coupon}
                    onChange={(e) => {
                      setCoupon(e.target.value.toUpperCase());
                      // Al editar, el preview previo deja de corresponder: lo limpiamos para no
                      // mostrar un descuento de un código que ya no está tecleado.
                      if (couponPreview) {
                        setCouponPreview(null);
                        setCouponPreviewCode('');
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleApplyCoupon();
                      }
                    }}
                    placeholder="Código (opcional)"
                    autoComplete="off"
                    autoFocus
                    style={{
                      flex: 1,
                      minWidth: 0,
                      boxSizing: 'border-box',
                      padding: '10px 12px',
                      fontSize: 14,
                      fontFamily: 'Poppins, sans-serif',
                      color: V4.primary,
                      background: V4.white,
                      border: `1px solid ${V4.grayLight}`,
                      borderRadius: 8,
                      outline: 'none',
                      textTransform: 'uppercase',
                    }}
                  />
                  <Button variant="secondary" onClick={handleApplyCoupon} disabled={!normalizedCoupon || couponLoading}>
                    {couponLoading ? 'Validando…' : 'Aplicar'}
                  </Button>
                </div>

                {/* Resultado inline del preview. Solo se muestra si corresponde al código tecleado. */}
                {previewMatchesInput && couponPreview && <CouponPreviewResult preview={couponPreview} />}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setCouponExpanded(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  margin: '0 0 16px',
                  padding: 0,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: 13,
                  fontWeight: 600,
                  color: V4.accent,
                }}
              >
                <IconPlus size={15} stroke={2.2} />
                Agregar código de descuento
              </button>
            )}

            {/* Mientras el preview del cupón está en vuelo bloqueamos el CTA: si no, el dueño podría
                continuar al pago (crea la suscripción y avanza al form de Stripe) ANTES de ver el
                resultado de la validación, anulando el preview que es el propósito de este paso. */}
            <Button variant="primary" fullWidth onClick={handleContinueToPayment} disabled={couponLoading}>
              {couponLoading ? 'Validando código…' : 'Continuar al pago'}
            </Button>
            <button
              type="button"
              onClick={handleClose}
              style={{
                display: 'block',
                width: '100%',
                marginTop: 10,
                padding: '6px 0',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'Poppins, sans-serif',
                fontSize: 13,
                color: V4.grayUltraDark,
              }}
            >
              Cancelar
            </button>
          </>
        )}

        {cardConfigured && isLoading && !clientSecret && (
          <p style={{ margin: 0, fontSize: 13, color: V4.grayUltraDark }}>Preparando tu pago…</p>
        )}

        {cardConfigured && error && !isLoading && (
          <>
            <p style={{ margin: '0 0 16px', fontSize: 12, color: V4.danger, lineHeight: 1.4 }} role="alert">
              {error}
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={handleClose}>
                Cancelar
              </Button>
              <Button variant="primary" onClick={handleRetry}>
                Reintentar
              </Button>
            </div>
          </>
        )}

        {cardConfigured && clientSecret && (
          <>
            <p
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                margin: '0 0 16px',
                fontSize: 12,
                color: V4.grayUltraDark,
              }}
            >
              <IconLock size={14} stroke={1.8} />
              Pago seguro procesado por Stripe.
            </p>
            <StripePaymentForm clientSecret={clientSecret} onSuccess={onActivated} onCancel={handleClose} />
          </>
        )}
      </div>
    </Modal>
  );
};
